import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/utils';

const CurrencyContext = createContext(null);

// Module-level cache so components outside the provider can still access rates
let _globalCurrencyData = {
  customerCurrency: null,
  storeCurrency: 'EUR',
  rates: null,
  showConversion: false,
};

// Language → default currency mapping (client-side fallback)
const LANG_CURRENCY = {
  fr: 'EUR', en: 'USD', es: 'EUR', pt: 'BRL', ar: 'SAR',
  zh: 'CNY', ru: 'RUB', de: 'EUR', it: 'EUR', ja: 'JPY',
  ko: 'KRW', hi: 'INR', tr: 'TRY', nl: 'EUR', pl: 'PLN',
  sw: 'KES', ha: 'NGN', yo: 'NGN', ig: 'NGN', am: 'ETB',
  zu: 'ZAR', wo: 'XOF',
};

// Currency symbols for compact display
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', KRW: '₩',
  INR: '₹', BRL: 'R$', RUB: '₽', TRY: '₺', PLN: 'zł', SEK: 'kr',
  NOK: 'kr', DKK: 'kr', CHF: 'CHF', AUD: 'A$', CAD: 'C$', NZD: 'NZ$',
  MXN: 'MX$', ARS: 'AR$', CLP: 'CL$', COP: 'CO$', PEN: 'S/',
  NGN: '₦', GHS: 'GH₵', KES: 'KSh', ZAR: 'R', EGP: 'E£',
  MAD: 'MAD', TND: 'TND', SAR: 'SAR', AED: 'AED', QAR: 'QAR',
  XOF: 'CFA', XAF: 'FCFA', ETB: 'ETB', THB: '฿', VND: '₫',
  PHP: '₱', MYR: 'RM', SGD: 'S$', IDR: 'Rp', PKR: '₨',
  BDT: '৳', LKR: 'Rs', TZS: 'TSh', UGX: 'USh', RWF: 'RF',
  HUF: 'Ft', RON: 'lei', CZK: 'Kč', UAH: '₴', IQD: 'IQD',
  JOD: 'JOD', KWD: 'KWD', BHD: 'BHD', OMR: 'OMR', LBP: 'LBP',
};

export const CurrencyProvider = ({ children, storeCurrency = 'EUR' }) => {
  const { i18n } = useTranslation();
  const [customerCurrency, setCustomerCurrency] = useState(null);
  const [rates, setRates] = useState(null);
  const fetchedRef = useRef(false);

  // Detect customer currency + fetch exchange rates on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const init = async () => {
      // 1. Detect currency from language + IP
      let detectedCurrency = LANG_CURRENCY[i18n.language] || 'EUR';
      try {
        const res = await api.get(`/public/detect-currency?lang=${i18n.language}`);
        if (res.data?.currency) detectedCurrency = res.data.currency;
      } catch { /* use language fallback */ }
      setCustomerCurrency(detectedCurrency);

      // 2. Fetch exchange rates (base = store currency)
      if (detectedCurrency !== storeCurrency) {
        try {
          const ratesRes = await api.get(`/public/exchange-rates?base=${storeCurrency}`);
          if (ratesRes.data?.rates) setRates(ratesRes.data.rates);
        } catch { /* no conversion available */ }
      }
    };

    init();
  }, [i18n.language, storeCurrency]);

  // Re-detect when language changes
  useEffect(() => {
    const newCurrency = LANG_CURRENCY[i18n.language] || customerCurrency || 'EUR';
    if (newCurrency !== customerCurrency) {
      setCustomerCurrency(newCurrency);
      // Fetch rates if needed
      if (newCurrency !== storeCurrency && !rates) {
        api.get(`/public/exchange-rates?base=${storeCurrency}`)
          .then(res => { if (res.data?.rates) setRates(res.data.rates); })
          .catch(() => {});
      }
    }
  }, [i18n.language]);

  const showConversion = customerCurrency && customerCurrency !== storeCurrency && rates;

  // Sync to module-level cache for components outside the provider
  useEffect(() => {
    _globalCurrencyData = { customerCurrency, storeCurrency, rates, showConversion: !!showConversion };
  }, [customerCurrency, storeCurrency, rates, showConversion]);

  const convertPrice = useCallback((amount, fromCurrency) => {
    if (!rates || !customerCurrency || customerCurrency === fromCurrency) return null;
    const rate = rates[customerCurrency];
    if (!rate) return null;
    return amount * rate;
  }, [rates, customerCurrency]);

  const getSymbol = useCallback((code) => {
    return CURRENCY_SYMBOLS[code] || code;
  }, []);

  const formatConverted = useCallback((amount) => {
    if (amount == null || !customerCurrency) return '';
    const sym = CURRENCY_SYMBOLS[customerCurrency] || customerCurrency;
    // Round smartly: no decimals for large currencies (JPY, KRW, etc.)
    const noDecimal = ['JPY', 'KRW', 'VND', 'IDR', 'HUF', 'CLP', 'UGX', 'TZS', 'RWF', 'IQD', 'LBP'];
    const decimals = noDecimal.includes(customerCurrency) ? 0 : 2;
    const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `≈ ${sym} ${formatted}`;
  }, [customerCurrency]);

  return (
    <CurrencyContext.Provider value={{ customerCurrency, storeCurrency, showConversion, convertPrice, formatConverted, getSymbol, rates }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrencyConversion = () => {
  const ctx = useContext(CurrencyContext);
  if (ctx) return ctx;

  // Fallback: use module-level global cache (for components outside provider)
  const g = _globalCurrencyData;
  const convertPrice = (amount, fromCurrency) => {
    if (!g.rates || !g.customerCurrency || g.customerCurrency === fromCurrency) return null;
    const rate = g.rates[g.customerCurrency];
    return rate ? amount * rate : null;
  };
  const formatConverted = (amount) => {
    if (amount == null || !g.customerCurrency) return '';
    const sym = CURRENCY_SYMBOLS[g.customerCurrency] || g.customerCurrency;
    const noDecimal = ['JPY', 'KRW', 'VND', 'IDR', 'HUF', 'CLP', 'UGX', 'TZS', 'RWF', 'IQD', 'LBP'];
    const decimals = noDecimal.includes(g.customerCurrency) ? 0 : 2;
    const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `≈ ${sym} ${formatted}`;
  };
  return {
    customerCurrency: g.customerCurrency,
    storeCurrency: g.storeCurrency,
    showConversion: g.showConversion,
    convertPrice,
    formatConverted,
    getSymbol: (code) => CURRENCY_SYMBOLS[code] || code,
    rates: g.rates,
  };
};
